import React, { useEffect, useState } from "react";
import { endOfYear, format, startOfYear, subMonths } from "date-fns";
import {
  getPresetKeyForRange,
  mergeDisabledWithNoFuture,
  normalizeDate,
  parseInputDate,
} from "./dateRangeUtils";
import type { Matcher } from "react-day-picker";

export interface UseDateRangePickerParams {
  onChange: (from?: Date, to?: Date) => void;
  onApply?: (from?: Date, to?: Date) => void;
  onReset?: () => void;
  defaultFrom?: Date;
  defaultTo?: Date;
  defaultMonthFrom?: Date;
  to?: Date;
  from?: Date;
  disabledDays?: Matcher | Matcher[] | undefined;
  allowFuture?: boolean;
}

export interface UseDateRangePickerResult {
  innerFrom?: Date;
  innerTo?: Date;
  initialMonthFrom: Date;
  activePreset: string | null;
  inputFrom: string;
  inputTo: string;
  setInputFrom: (value: string) => void;
  setInputTo: (value: string) => void;
  handleRelativePreset: (monthsBack: number, key: string) => void;
  handleYearPreset: (year: number) => void;
  commitInputChange: (which: "from" | "to") => void;
  openFor: "from" | "to" | null;
  anchorEl: HTMLElement | null;
  handleFieldIconClick: (
    which: "from" | "to"
  ) => (event: React.MouseEvent<HTMLElement>) => void;
  handlePopoverClose: () => void;
  handleDaySelect: (date?: Date) => void;
  handleApplyClick: () => void;
  handleResetClick: () => void;
  disabledMatchers: Matcher[];
}

export const useDateRangePicker = (
  params: UseDateRangePickerParams
): UseDateRangePickerResult => {
  const {
    to,
    from,
    defaultFrom,
    defaultTo,
    defaultMonthFrom,
    onChange,
    onApply,
    onReset,
    disabledDays,
    allowFuture = false,
  } = params;

  const [initialMonthFrom] = useState<Date>(
    (defaultMonthFrom ?? defaultFrom ?? from ?? new Date()) as Date
  );
  const [innerFrom, setInnerFrom] = useState<Date | undefined>(
    from || defaultFrom
  );
  const [innerTo, setInnerTo] = useState<Date | undefined>(to || defaultTo);
  const [activePreset, setActivePreset] = useState<string | null>(null);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [openFor, setOpenFor] = useState<"from" | "to" | null>(null);

  const formattedFrom = innerFrom && format(innerFrom, "dd.MM.yyyy");
  const formattedTo = innerTo && format(innerTo, "dd.MM.yyyy");

  const [inputFrom, setInputFrom] = useState<string>(formattedFrom ?? "");
  const [inputTo, setInputTo] = useState<string>(formattedTo ?? "");

  useEffect(() => {
    setInputFrom(formattedFrom ?? "");
  }, [formattedFrom]);

  useEffect(() => {
    setInputTo(formattedTo ?? "");
  }, [formattedTo]);

  const handleApplyClick = () => {
    if (!innerFrom) return;
    onApply?.(innerFrom, innerTo);
  };

  const handleResetClick = () => {
    setInnerFrom(undefined);
    setInnerTo(undefined);
    setActivePreset(null);
    onChange(undefined, undefined);
    onReset?.();
  };

  const applyRange = (fromDate: Date, toDate: Date, presetKey: string) => {
    setActivePreset(presetKey);
    setInnerFrom(fromDate);
    setInnerTo(toDate);
    onChange(fromDate, toDate);
  };

  const handleRelativePreset = (monthsBack: number, key: string) => {
    const end = new Date();
    const start = subMonths(end, monthsBack);
    applyRange(start, end, key);
  };

  const handleYearPreset = (year: number) => {
    const now = new Date();
    const start = startOfYear(new Date(year, 0, 1));
    const end =
      !allowFuture && year === now.getFullYear() && now < endOfYear(start)
        ? now
        : endOfYear(start);
    applyRange(start, end, `year-${year}`);
  };

  const handleFieldIconClick =
    (which: "from" | "to") => (event: React.MouseEvent<HTMLElement>) => {
      setOpenFor(which);
      setAnchorEl(event.currentTarget);
    };

  const commitInputChange = (which: "from" | "to") => {
    const rawValue = which === "from" ? inputFrom : inputTo;

    if (!rawValue.trim()) {
      if (which === "from") {
        const nextFrom: Date | undefined = undefined;
        const nextTo = innerTo;
        setInnerFrom(nextFrom);
        setActivePreset(getPresetKeyForRange(nextFrom, nextTo));
        onChange(nextFrom, nextTo);
      } else {
        const nextTo: Date | undefined = undefined;
        const nextFrom = innerFrom;
        setInnerTo(nextTo);
        setActivePreset(getPresetKeyForRange(nextFrom, nextTo));
        onChange(nextFrom, nextTo);
      }
      return;
    }

    const parsedRaw = parseInputDate(rawValue);
    if (!parsedRaw) {
      if (which === "from") {
        setInputFrom(formattedFrom ?? "");
      } else {
        setInputTo(formattedTo ?? "");
      }
      return;
    }

    const today = normalizeDate(new Date());
    const parsed = normalizeDate(parsedRaw);

    if (!allowFuture && parsed > today) {
      if (which === "from") {
        setInputFrom(formattedFrom ?? "");
      } else {
        setInputTo(formattedTo ?? "");
      }
      return;
    }

    if (which === "from") {
      const nextFrom = parsed;
      let nextTo = innerTo;
      if (nextTo && nextTo < nextFrom) {
        nextTo = nextFrom;
      }
      setInnerFrom(nextFrom);
      setInnerTo(nextTo);
      const presetKey = getPresetKeyForRange(nextFrom, nextTo);
      setActivePreset(presetKey);
      onChange(nextFrom, nextTo);
    } else {
      let nextTo = parsed;
      let nextFrom = innerFrom;
      if (nextFrom && nextTo < nextFrom) {
        nextFrom = nextTo;
        setInnerFrom(nextFrom);
      }
      setInnerTo(nextTo);
      const presetKey = getPresetKeyForRange(nextFrom, nextTo);
      setActivePreset(presetKey);
      onChange(nextFrom, nextTo);
    }
  };

  const handleDaySelect = (date?: Date) => {
    if (!date || !openFor) return;

    if (openFor === "from") {
      const nextFrom = date;
      let nextTo = innerTo;
      if (nextTo && nextTo < nextFrom) {
        nextTo = nextFrom;
      }
      setInnerFrom(nextFrom);
      setInnerTo(nextTo);
      const presetKey = getPresetKeyForRange(nextFrom, nextTo);
      setActivePreset(presetKey);
      onChange(nextFrom, nextTo);
    } else if (openFor === "to") {
      let nextTo = date;
      let nextFrom = innerFrom;
      if (nextFrom && nextTo < nextFrom) {
        nextFrom = nextTo;
        setInnerFrom(nextFrom);
      }
      setInnerTo(nextTo);
      const presetKey = getPresetKeyForRange(nextFrom, nextTo);
      setActivePreset(presetKey);
      onChange(nextFrom, nextTo);
    }

    setOpenFor(null);
    setAnchorEl(null);
  };

  const handlePopoverClose = () => {
    setOpenFor(null);
    setAnchorEl(null);
  };

  const disabledMatchers = allowFuture
    ? Array.isArray(disabledDays)
      ? disabledDays
      : disabledDays
        ? [disabledDays]
        : []
    : mergeDisabledWithNoFuture(disabledDays);

  useEffect(() => {
    setActivePreset(getPresetKeyForRange(innerFrom, innerTo));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    innerFrom,
    innerTo,
    initialMonthFrom,
    activePreset,
    inputFrom,
    inputTo,
    setInputFrom,
    setInputTo,
    handleRelativePreset,
    handleYearPreset,
    commitInputChange,
    openFor,
    anchorEl,
    handleFieldIconClick,
    handlePopoverClose,
    handleDaySelect,
    handleApplyClick,
    handleResetClick,
    disabledMatchers,
  };
};
